import { ReceiptMutationInput, ReceiptModel } from 'api-hooks/receipt';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form } from 'components/elements';
import Text from 'components/elements/text';
import FormHeader from 'components/widgets/form-header';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import { FormDivider, SeparatorVertical } from 'legacy-modules/styles';
import { ReceiptTableForm } from 'legacy-modules/transactions/component/table-form-components';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import ReceiptActionComponent from './action-component';
import { receiptSchema } from './form-type';
import InformationField from './information-field';
import useGetDefaultValues from '../hooks/use-get-default-values';

interface Props {
  receipt?: ReceiptModel;
  onSubmit: (
    input: ReceiptMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<ReceiptModel | undefined>;
}

export default function ReceiptForm(props: Props) {
  const { t } = useTranslation();
  const { asPath } = useRouter();
  const { receipt } = props;

  const defaultValues = useGetDefaultValues(receipt);

  const resolver = useYupValidationResolver(receiptSchema());

  const methods = useForm({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values) => {
      try {
        const receiptDetails = values.receiptDetails.filter(
          (detail) => detail.isCheck,
        );

        if (receiptDetails.length <= 0) {
          methods.setError('receiptDetails', {
            message: t('error:at_least_select_one_item'),
          });
          return;
        }

        values.needApproval =
          values.transactionAt.getTime() !== values.tempTransactionAt.getTime();

        await props.onSubmit({ ...values, receiptDetails }, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props, t],
  );

  const onRenderRightComponent = React.useMemo(() => {
    if (receipt) {
      return (
        <>
          <ReceiptActionComponent receipt={receipt} />
        </>
      );
    }
    return undefined;
  }, [receipt]);

  const customViewName =
    receipt && [t('common:receipt'), receipt.number].join(' - ');

  const customerId = methods.getValues('customerId');
  React.useEffect(() => {
    if (!customerId || customerId === '') {
      methods.reset();
    }
  }, [customerId, methods]);

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!receipt}
      customViewName={customViewName}
      keyName={asPath}
    >
      <FormHeader
        data={receipt}
        title={
          receipt
            ? customViewName ?? t('common:receipt')
            : t('common:add_extra', { extra: t('common:receipt') })
        }
        noEdit
        noDelete
        onRenderRightComponent={onRenderRightComponent}
      />
      <div>
        <InformationField isFilled={!!receipt} />
        <SeparatorVertical $margin={36} />
        {methods?.formState?.errors?.receiptDetails && (
          <>
            <Text variant="LabelMedium" color="red">
              {methods?.formState?.errors?.receiptDetails.message}
            </Text>
            <Separator gap={8} />
          </>
        )}
        <ReceiptTableForm isFilled={!!receipt} />
        <FormDivider />
      </div>
    </Form>
  );
}
